'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Definiere die Struktur der Brands-Daten
interface BrandsData {
  title: string;
  groups: string[][];
}

interface BrandsShowcaseProps {
  brandsData: BrandsData;
}

export function BrandsShowcase({ brandsData }: BrandsShowcaseProps) {
  const [activeSection, setActiveSection] = useState(0);

  // Marken-Rotation-Effekt
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSection(prev => (prev + 1) % brandsData.groups.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [brandsData.groups.length]);

  return (
    <motion.div
      className="mb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 1.2 }}
    >
      <motion.h2
        className="text-2xl md:text-3xl font-bold mb-8 text-center text-[#88ffff]"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.3 }}
      >
        {brandsData.title}
      </motion.h2>

      <div className="relative overflow-hidden bg-[#0C0B32]/30 backdrop-blur-sm rounded-xl border border-[#88ffff]/10">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            className="flex flex-wrap items-center justify-center gap-4 p-6 pb-10 min-h-[120px]"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
          >
            {brandsData.groups[activeSection].map((brand: string, index: number) => (
              <motion.div
                key={brand}
                className="text-white/80 text-sm sm:text-base md:text-lg font-medium hover:text-[#88ffff] transition-colors px-4 py-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.1 }}
              >
                {brand}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {brandsData.groups.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSection(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                activeSection === i ? "bg-[#88ffff]" : "bg-white/30"
              }`}
              aria-label={`Markengruppe ${i+1}`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}